import { Node, Tree } from "utils/tree";
import { Layout as PlotlyLayout} from "plotly.js";
import { IconName } from "@blueprintjs/core";

// Values

/**
 * Used to contain all of the markdown.
 */
export type IMarkdown = string;

/**
 * Unique name that references a template component and may also be used as a model or result field name.
 * This field name can contain nested values using a dot separator and/or array values in square brackets.
 * Individual name parts can use uppcase, lowercase, and underscore only.
 * E.G. my.deeply[7].nested.value
 */
export type IName = string;

/**
 * A path to data within the data model or result.
 * Changed from [IName, number, IName] to `${IName}[${number}].${IName}` in order to support nested values.
 */
export type IPath = string;

/**
 * A Javascript expression with access to only locally scoped variables.
 */
export type IExpression = string;

/**
 * A model data value for a server default or user uploaded file.
 */
export type IUpload = {
  mimetype: string;
  filename: string;
  path?: string;
  value?: string;
  fault?: string;
};

export type IPosition = [number, number];

export interface ILocationValue {
  label: string;
  position?: IPosition;
  placeId?: string;
}

export interface ILocationMatrixPair {
  fromIndex: number;
  toIndex: number;
  distanceMeters: number;
  durationSeconds: number;
  geometry: Array<IPosition>;
}

export interface ILocationMatrixValue {
  locationSignature: string;
  distanceMatrix: Array<Array<number>>;
  durationMatrix: Array<Array<number>>;
  pairs: Array<ILocationMatrixPair>;
  calculatedAt?: string;
}

// Actions

/**
 * A data model input value.
 */
export type IValue =
  | number
  | string
  | boolean
  | IUpload
  | ILocationValue
  | ILocationMatrixValue
  | Array<number | string>
  | Array<Array<number>>
  | Array<IModel>
  | [number, number];

/**
 * Callback used to change a data model field value.
 */
export type IChange = (path: IPath, value: IValue) => void;

/**
 * Callback used to add an item to an input list.
 */
export type ICreate = (list: IList) => void;
/**
 * Callback used to remove an item from an input list.
 */
export type IRemove = (list: IList, index: number) => void;

/**
 * Callback used to set hidden template nodes.
 */
export type IHidden = (paths: Array<IPath>, value: boolean) => void;

/**
 * Used to set the value of a property for the specified path
 */
export type IProperty = (path: IPath, value: any) => void;

/**
 * Callback used to set overridden template nodes.
 */
export type IOverridden = (path: IPath, value: boolean) => void;

/**
 * Callback used to get a data model field value.
 */
export type IGetValue = (path: IPath, model?: IModel, result?: IResult) => IValue;

/**
 * Callback used to set a stored model value for the specified path.
 */
export type IStoredChange = (path: IPath, value: IValue) => void;

/**
 * Callback used to get the entire path of the field value.
 */
export type IGetPath = (path: IPath) => IPath;

/**
 * Callback used to get a data model hidden value.
 */
export type IGetHidden = (path: IPath) => boolean;

/**
 * Callback used to get a data model overridden value.
 */
export type IGetOverridden = (path: IPath) => boolean;

/**
 * Used to get the input, output, or layout template for the specified path.
 */
export type IGetElement = (name: IName) => Node<IInput | IOutput | ILayout<IBase> | undefined>;

/**
 * Used to get the value of a property for the specified path.
 */
export type IGetProperty = (path: IPath) => any;

/**
 * An action that defines a list of data model source fields and an expression.
 */
export interface IAction {
  sources: Array<IName>;
  expression: IExpression;
}

/**
 * An action that is used to disable a template component.
 */
export type IDisable = boolean | IAction;

/**
 * An action that is used to validate a field and display a message when validation fails.
 */
export interface IValidate extends IAction {
  message: IExpression;
}

/**
 * Query that retrieves data from an endpoint.
 */
export interface IQuery {
  method?: "get" | "post";
  url?: IAction | string;
  body?: IAction | string;
  transform?: IExpression | IAction; // In addition to ones specified in `sources`, the argument `data` is available for response data from remote fetch.
}

// Types

/**
 * Base component that is used to display a label and extra info (typically the unit of measure) to the user.
 */
export interface IDisplay {
  label?: string;
  info?: string;
}

/**
 * Displayable component that can be interacted with and may also have a description or be hidden.
 */
export interface IBase extends IDisplay {
  type: string;
  name: IName;
  description?: string;
  hidden?: IAction;
}

/**
 * Input component that can be disabled, validated, and has a default value.
 * If default is specified then the value can be undefined.
 */
export interface IInput extends IBase {
  type: "number" | "text" | "select" | "checkbox" | "file" | "list" | "radio" | "range" | "location" | "location_matrix" | "calculate";
  disable?: IDisable;
  validate?: IValidate;
  default?: IAction;
  value?: IValue;
}

/**
 * Output component for conveying results to the user.
 */
export interface IOutput extends IBase {
  type: "3d_surface" | "bar_chart" | "waterfall_chart" | "pie_chart" | "time_chart" | "raw_plotly_chart";
  variant?: "default" | "minimal" | "reduced" | "overview";
  height?: number;
}

/**
 * Base type for a layout component.
 */
export interface ILayout<T> extends IBase {
  type: "group" | "tabs" | "tab" | "explorer" | "anchors" | "label" | "image";
  items: Array<T>;
  disable?: IDisable;
  validate?: IValidate;
}

// Using exact type arrays causes issues with JSON schema parsing.

/**
 * Composite type for the inputs.
 */
// export type IInputs = INumber | IText | ISelect | ICheckbox | IFile | IList | IRadio | IRange | ICalculate;

/**
 * Composite type for the outputs.
 */
// export type IOutputs = I3dSurface | IBarChart | IWaterfallChart | IPieChart | ITimeChart;

/**
 * Composite type for the layouts.
 */
// export type ILayouts = IGroup | ITabs | IExplorer | IAnchors | ILabel | IImage;

/**
 * Composite type for all inputs, outputs, and layouts.
 */
// export type IBases = IInputs | IOutputs | ILayouts;

// Inputs

/**
 * Input component for numbers.
 */
export interface INumber extends IInput {
  type: "number";
  min: number;
  max: number;
  step: number;
  value: number;
  leftIcon?: IconName;
}

/**
 * Input component for raw text.
 */
export interface IText extends IInput {
  type: "text";
  variant?: "text" | "area";
  value: string;
}

/** An address or place resolved to persistent map coordinates. */
export interface ILocation extends IInput {
  type: "location";
  value: ILocationValue;
  center?: IPosition;
}

/** A read-only map and symmetric driving matrix derived from fixed or model-driven locations. */
export interface ILocationMatrix extends IInput {
  type: "location_matrix";
  value: ILocationMatrixValue;
  locations: Array<ILocationValue> | IAction;
  center?: IPosition;
  zoom?: number;
}

/**
 * Option for a select or combo box.
 */
export interface ISelectOption {
  value: string;
  label: string;
}

/**
 * Input component for a select or combo box.
 */
export interface ISelect extends IInput {
  type: "select";
  options?: Array<ISelectOption>;
  query?: IQuery;
  value?: string | number;
}

/**
 * Input component for a checkbox.
 */
export interface ICheckbox extends IInput {
  type: "checkbox";
  value: boolean;
}

/**
 * Input component for a user uploaded file.
 */
export interface IFile extends IInput {
  type: "file";
  mimetypes?: Array<string>;
  value: IUpload;
}

/**
 * Input component for a variable list of inputs. Implements IGroupBehavior.
 */
export interface IList extends IGroupBehavior, IInput {
  type: "list";
  item_label: "string";
  items: Array<IBase>;
  min: number;
  max: number;
}

/**
 * Option for a radio item.
 */
export interface IRadioOption {
  value: string | number;
  label: string;
  description?: string;
  disable?: IDisable;
}

/**
 * Input component for radio.
 */
export interface IRadio extends IInput {
  type: "radio";
  options?: Array<IRadioOption>;
  value: string | number;
}

/**
 * Input component for range.
 */
export interface IRange extends IInput {
  type: "range";
  min?: number | IAction;
  max?: number | IAction;
  step?: number;
  vertical?: boolean;
  value: [number, number];
  variant: "text" | "slider";
}

/**
 * Non visual input component that can assign a model value automatically.
 */
export interface ICalculate extends IInput {
  type: "calculate";
  query?: IQuery;
}

// Outputs

/**
 * Used to define a data structure stored in the result model.
 */
export interface IStructure {
  type: "structure";
  name: IName;
  value: IName;
  label: IName;
}

/**
 * Used to define how data is stored in the result model.
 */
export interface IData extends IDisplay {
  name: IName;
  value?: number | string;
}

/**
 * Data used to store time series data at the defined interval.
 */
export interface ITimeData extends IData {
  interval: number;
}

/**
 * Defines a figure (collection of data) for a chart.
 */
export interface IFigure {
  label?: string;
  info?: string;
}

/**
 * Defines a figure with a single axis.
 */
export interface IDataFigure extends IFigure {
  data: Array<IData> | IStructure;
}

/**
 * Used to define a single data structure for 2d data.
 */
export interface I2dStructure extends IFigure {
  type: "structure";
  name: IName;
  x_value: IName;
  y_value: IName;
}

/**
 * Used to define a single data structure for waterfall data.
 */
export interface IWaterfallStructure extends I2dStructure {
  m_value?: IName;
}

/**
 * Defines a figure with 2 axis.
 */
export interface I2dFigure extends IFigure {
  x_data: IData;
  y_data: IData;
}

/**
 * Defines a figure for a waterfall chart.
 */
export interface IWaterfallFigure extends I2dFigure {
  m_data?: IData;
}

/**
 * Used to define a single data structure for 2d data.
 */
export interface I3dStructure extends I2dStructure {
  z_value: IName;
}

/**
 * Defines a figure with 3 axis.
 */
export interface I3dFigure extends I2dFigure {
  z_data: IData;
}

/**
 * Output component used to display a surface in 3d space.
 */
export interface I3dSurface extends IOutput {
  type: "3d_surface";
  axis?: Array<IDisplay>;
  range?: Array<IName>;
  figures: Array<I3dFigure | I3dStructure>;
}

/**
 * Output component used to display a bar chart.
 */
export interface IBarChart extends IOutput {
  type: "bar_chart";
  stack?: boolean;
  horizontal?: boolean;
  axis?: Array<IData>;
  range?: Array<IName>;
  figures: Array<I2dFigure | I2dStructure>;
}

/**
 * Output component used to display a raw Plotly.js chart.
 */
export interface IRawPlotlyChart extends IOutput {
  type: "raw_plotly_chart";
  dataName?: IName;
  data?: IAction;
  layoutName?: IName;
  layout?: Partial<PlotlyLayout> | IAction;
}

/**
 * Output component used to display a waterfall chart.
 */
export interface IWaterfallChart extends IOutput {
  type: "waterfall_chart";
  concatenate?: boolean;
  horizontal?: boolean;
  measure?: Array<"absolute" | "relative" | "total">;
  axis?: Array<IData>;
  range?: Array<IName>;
  figures: Array<IWaterfallFigure | IWaterfallStructure>;
}

/**
 * Output component for a pie chart.
 */
export interface IPieChart extends IOutput {
  type: "pie_chart";
  stack: boolean;
  figures: Array<IDataFigure>;
}

/**
 * Defines a figure for time series data.
 */
export interface ITimeFigure extends IFigure {
  start?: string;
  startname?: IName;
  data: Array<ITimeData>;
}

/**
 * Axis types for a time chart.
 */
export type ITimeAxis = "x_and_y" | "x_only" | "none";

/**
 * Output component for a time chart.
 */
export interface ITimeChart extends IOutput {
  type: "time_chart";
  zoom?: ITimeAxis;
  sync?: ITimeAxis;
  axis?: Array<IData>;
  range?: Array<IName>;
  figures: Array<ITimeFigure>;
  initial?: { range?: Array<IName> };
}

// Layouts

/**
 * Layout type for a scrollable view with a left side navigation pane.
 */
export interface IAnchors extends ILayout<IBase> {
  type: "anchors";
}

/**
 * Defines how a group of items should be displayed.
 * The optional layout is for the sub components of the group.
 * The specified numbers represent the share of the extra space for each component.
 */
export interface IGroupBehavior {
  inline?: boolean;
  layout?: Array<number>;
}

/**
 * Layout type for a list of components. Implements IGroupBehavior.
 */
export interface IGroup extends IGroupBehavior, ILayout<IBase> {
  type: "group";
}

/**
 * Defines a tab within a tabs layout.
 */
export interface ITab extends ILayout<IBase> {
  type: "tab";
}

/**
 * Layout for a tabbed list of components.
 */
export interface ITabs extends ILayout<ITab> {
  type: "tabs";
  vertical?: boolean;
}

/**
 * Layout for an explorer like view where items can be hidden or shown.
 * Specify includes or excludes to specify which children should be controlled by this explorer.
 */
export interface IExplorer extends ILayout<IBase> {
  type: "explorer";
  variant: "tree" | "list";
  includes?: Array<IName>;
  excludes?: Array<IName>;
}

/**
 * Layout component that can display text using a custom formatter and display as invalid (red) or verified (green).
 */
export interface ILabel extends ILayout<IBase> {
  type: "label";
  variant?: "text" | "small" | "large" | "area" | "input" | "markdown";
  format?: IAction;
  validate?: IValidate;
  verify?: IValidate;
  width?: number;
}

/**
 * Layout component that can display an image.
 */
export interface IImage extends ILayout<IBase> {
  type: "image";
  url: IAction | string;
  width?: string;
  height?: string;
}

// Containers

/**
 * The API for the service.
 */
export interface IApi {
  url: Array<string>;
  endpoint: Array<string>;
  key: Array<string>;
  output: Array<string>;
}

/**
 * Defines the entire template.
 */
export interface ITemplate {
  id?: number | string;
  name: IName;
  label: string;
  description: string;
  brief: string;
  feature: string;
  keywords?: Array<string>;
  icon?: string;
  markdown?: IMarkdown;
  apis?: IApi;
  definitions?: { [property: string]: any };
  inputs: Array<ILayout<IBase>>;
  outputs: Array<ILayout<IBase>>;
}

/**
 * The data model used to store input values.
 */
export interface IModel {
  [name: IName]: IValue;
}

/**
 * The data model used to store the result values.
 */
export interface IResult {
  [name: IName]: IValue;
}

/**
 * Describes if the side effect is on an input or output.
 */
export enum SideEffectType {
  Input,
  Output,
}

/**
 * Descriptor for an input with a side effect defined.
 */
export interface ISideEffect {
  template: IInput;
  getValue: IGetValue;
  path: IPath;
  type: SideEffectType;
}

/**
 * A list of side effect descriptors.
 */
export type ISideEffects = Array<ISideEffect>;

/**
 * Type for the properties object which can contain any additional information for the template UI.
 */
export interface IProperties {
  [name: IName]: any;
}

export interface IDependencyIndex {
  apiBackedFields: Array<IName>;
  directDependencies: {
    [name: IName]: Array<IName>;
  };
  transitiveApiDependencies: {
    [name: IName]: Array<IName>;
  };
}

/**
 * The metadata for additional information related to a model.
 */
export interface IMetadata {
  hidden: Array<IName>;
  loading: Array<IName>;
  faults: {
    [name: IName]: string;
  };
  overridden: Array<IName>;
  effects: ISideEffects;
  inputs?: Tree<Partial<IBase>>;
  outputs?: Tree<Partial<IBase>>;
  properties: IProperties;
  dependencyIndex: IDependencyIndex;
  initializing: boolean;
  initialLoading: Array<string>;
  hiddenInput: Array<IName>;
}

/**
 * Action for setting or clearing a model component as loading.
 */
export type ILoading = (name: IName, loading: boolean) => void;

/**
 * Action for setting or clearing a model component fault.
 */
export type IFault = (name: IName, message?: string) => void;

/**
 * Handlers for mutating state or data.
 */
export interface IHandlers {
  handleChange?: IChange;
  handleCreate?: ICreate;
  handleRemove?: IRemove;
  handleHidden?: IHidden;
  handleOverridden?: IOverridden;
  handleLoading?: ILoading;
  handleFault?: IFault;
  handleProperty?: IProperty;
  handleStoredChange?: IStoredChange;
  handleAssistantContextTag?: (path: IPath, label?: string) => void;
  assistantContextModelName?: string;
}

/**
 * Data structure to hold data model mutators
 */
export interface IMutators extends IHandlers {
  getValue: IGetValue;
  getStoredValue?: IGetValue;
  setStoredValue?: IStoredChange;
  getPath: IGetPath;
  isHidden: IGetHidden;
  isOverridden: IGetOverridden;
  getElement?: IGetElement;
  getProperty?: IGetProperty;
}

/**
 * Context for a list item.
 */
export interface IListContext {
  name: IName;
  index: number;
}
